// State machine: idle -> upload -> edit -> saving -> saved. Owns the modal and the host hooks.

import { useEffect, useRef, useState } from 'react';
import { ApiError, fetchDesign, uploadDesign } from './api';
import type { Design, ImageObject, Orientation, TextObject } from './design';
import { Editor } from './Editor';
import { renderPng } from './export';
import { buildDesign, coverFit, covers, effectiveDpi, geometry, rescale, thirdFit, zoomAt } from './geometry';
import { heicToJpeg } from './heic';
import { attachHost, recallReference, rememberReference } from './host/adapter';
import { MAX_BYTES, SUPPORTED, isHeic, loadImage, sniff, type LoadedImage } from './image';
import type { Geometry } from './geometry';
import type { ResolvedOptions } from './main';
import { loadFonts, newText } from './text';
import { TextTools } from './TextTools';

type Phase = 'idle' | 'upload' | 'loading' | 'edit' | 'saving';

interface Saved { reference: string; previewUrl: string }

const MAX_IMAGES = 10;

export function App({ opts, mountEl }: { opts: ResolvedOptions; mountEl: HTMLElement }) {
  const t = opts.labels;
  // In the cart the design is done: the mount goes back to its empty state so the customer sees
  // the design left with the order, and a reload or a later visit starts empty too. Deferred a
  // tick: the theme's own click handler reads the reference field synchronously, and clearing it
  // during the same click would send an empty reference to the cart.
  const restartRef = useRef<() => void>(() => {});
  const host = useRef(
    attachHost(mountEl, opts.targetInputSelector, opts.addToCartSelector, opts.labels.buyBlocked,
      () => setTimeout(() => restartRef.current(), 0)),
  ).current;

  const [phase, setPhase] = useState<Phase>('idle');
  const [saved, setSaved] = useState<Saved | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [fontsVersion, setFontsVersion] = useState(0);
  const [ruler, setRuler] = useState(false);
  const [sizing, setSizing] = useState(false);

  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const g = geometry(opts.paper, orientation, opts.targetDpi);
  const [images, setImages] = useState<ImageObject[]>([]);
  const [loaded, setLoaded] = useState<Record<string, LoadedImage>>({});
  const blobs = useRef<Record<string, Blob>>({}); // the originals, uploaded on save
  const [texts, setTexts] = useState<TextObject[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const selectedText = texts.find((x) => x.id === selected) ?? null;
  const selectedImage = images.find((x) => x.id === selected) ?? images[0] ?? null; // zoom target
  const pickedImage = images.find((x) => x.id === selected) ?? null; // explicitly tapped, for the size panel
  useEffect(() => setSizing(false), [selected]);
  const extraInput = useRef<HTMLInputElement>(null);
  // Every added image is confirmed on its own: the panel opens with the box unticked each time.
  const [adding, setAdding] = useState(false);
  const [addRights, setAddRights] = useState(false);
  const pressedOverlay = useRef(false);

  // Att oppna en sparad design ar inte att spara den: `onDesignSaved` hor hemma i `save()`,
  // annars far varden aterkallet redan vid inladdning och tror att kunden tryckt Spara.
  const publish = (next: Saved | null) => {
    setSaved(next);
    host.setReference(next?.reference ?? null);
    rememberReference(opts.productId, next?.reference ?? null);
  };

  // Reload: the reference survives in sessionStorage; the design and originals come from the API.
  // `opts.reference` overrides it — that is how an admin page opens a customer's design.
  useEffect(() => {
    const ref = opts.reference ?? recallReference(opts.productId);
    if (!ref) { host.setReference(null); return; }
    fetchDesign(opts.apiBase, ref).then(async (d) => {
      publish({ reference: ref, previewUrl: d.previewUrl });
      const got: Record<string, LoadedImage> = {};
      for (const im of d.design.images) {
        const url = d.imageUrls[im.id];
        if (!url) continue;
        const b = await (await fetch(url)).blob();
        blobs.current[im.id] = b;
        got[im.id] = await loadImage(b);
      }
      setLoaded(got);
      setImages(d.design.images.filter((im) => got[im.id]));
      setOrientation(d.design.orientation);
      setTexts(d.design.texts);
    }).catch(() => publish(null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fonts: all eight the first time the modal opens, so the picker never shows a fallback face.
  useEffect(() => {
    if (phase !== 'idle') loadFonts().then(() => setFontsVersion((v) => v + 1));
  }, [phase !== 'idle']); // eslint-disable-line react-hooks/exhaustive-deps

  /** Any image, first or later. The first one covers the canvas; the rest land at a third. */
  const addImage = async (f: File) => {
    setError(null);
    if (images.length >= MAX_IMAGES) return setError(t.tooManyImages);
    if (f.size > MAX_BYTES) return setError(t.tooLarge);
    const format = await sniff(f);
    if (!SUPPORTED.includes(format) && !isHeic(f)) return setError(t.badFormat.replace('{format}', format));
    const first = images.length === 0;
    if (first) setPhase('loading');
    try {
      // Converted here when the browser can; the API converts server-side if this fails.
      if (isHeic(f)) f = await heicToJpeg(f).catch(() => f);
      // Weighed again: HEIC is about half the size of the JPEG it becomes, so a 48 Mpx photo
      // passes the check above and is over the limit by the time the API sees it. Finding that
      // out at save time cost the customer the whole design.
      if (f.size > MAX_BYTES) {
        if (first) setPhase('upload');
        return setError(t.tooLarge);
      }
      const src = await loadImage(f);
      const id = `i${Date.now().toString(36)}`;
      const im = first ? coverFit(id, src.width, src.height, g) : thirdFit(id, src.width, src.height, g);
      blobs.current[id] = f;
      setLoaded({ ...loaded, [id]: src });
      setImages([...images, im]);
      setSelected(first ? null : id);
      setPhase('edit');
    } catch {
      setError(t.brokenImage);
      if (first) setPhase('upload');
    }
  };

  const updateImage = (im: ImageObject) => setImages(images.map((x) => (x.id === im.id ? im : x)));
  const updateText = (nt: TextObject) => setTexts(texts.map((x) => (x.id === nt.id ? nt : x)));

  const switchOrientation = (o: Orientation) => {
    if (o === orientation) return;
    const next = geometry(opts.paper, o, opts.targetDpi);
    setOrientation(o);
    setImages(rescale(images, g, next));
    setTexts(rescale(texts, g, next));
  };

  const rotate = () => { if (selectedImage) updateImage({ ...selectedImage, rotation: (selectedImage.rotation + 90) % 360 }); };

  const addText = () => {
    const nt = newText(g, t.typeText);
    setTexts([...texts, nt]);
    setSelected(nt.id);
  };

  const removeSelected = () => {
    if (selectedText) setTexts(texts.filter((x) => x.id !== selected));
    else if (selected) {
      const rest = images.filter((x) => x.id !== selected);
      const { [selected]: _gone, ...others } = loaded;
      delete blobs.current[selected];
      setImages(rest);
      setLoaded(others);
      if (rest.length === 0) setPhase('upload'); // an image is mandatory
    }
    setSelected(null);
  };

  const save = async () => {
    if (images.length === 0) return;
    setPhase('saving'); setError(null); setProgress(0); setSelected(null);
    try {
      const design: Design = buildDesign(opts.productId, g, images, texts);
      const png = await renderPng(g, images, loaded, texts);
      const r = await uploadDesign(opts.apiBase, blobs.current, png, design, setProgress);
      publish({ reference: r.reference, previewUrl: r.previewUrl });
      opts.onDesignSaved?.(r.reference, r.previewUrl);
      setPhase('idle');
    } catch (e) {
      console.error('dc-cake-editor: save failed', e);
      setError(e instanceof ApiError && e.status === 429 ? t.tooMany : t.error);
      setPhase('edit');
    }
  };

  const restart = () => {
    publish(null);
    setImages([]); setLoaded({}); blobs.current = {}; setTexts([]); setSelected(null); setOrientation('portrait');
    setPhase('idle');
  };
  restartRef.current = restart;
  const restartInModal = () => { restart(); setPhase('upload'); };

  if (phase === 'idle') {
    return (
      <div className="mount">
        {saved ? (
          <>
            <img className="thumb" src={saved.previewUrl} alt="" />
            <span>{t.saved}</span>
            <button onClick={() => setPhase(images.length ? 'edit' : 'upload')}>{t.edit}</button>
            <button className="link" onClick={restart}>{t.restart}</button>
          </>
        ) : (
          <button className="primary" onClick={() => { setError(null); setPhase(images.length ? 'edit' : 'upload'); }}>{images.length ? t.edit : t.upload}</button>
        )}
      </div>
    );
  }

  // Lowest effective dpi across every image (same formula for all: target dpi / scale).
  const dpi = Math.min(Infinity, ...images.map((im) => effectiveDpi(im, g)));
  // ponytail: one image must cover on its own; a collage that only covers together still warns.
  const uncovered = !images.some((im) => covers(im, g));
  const checkered = images.some((im) => loaded[im.id]?.fakeTransparency);
  // Zoom buttons step 10 % around the middle of the sheet, so what sits in the centre of the
  // cake stays put. Buttons, not a slider: a slider's whole travel is a few mm of thumb.
  const zoom = (f: number) => { if (selectedImage) updateImage(zoomAt(selectedImage, f, { x: g.outW / 2, y: g.outH / 2 })); };
  const sizeMin = Math.round(Math.min(g.outW, g.outH) * 0.02), sizeMax = Math.round(Math.min(g.outW, g.outH) * 0.4);
  const editing = (phase === 'edit' || phase === 'saving') && images.length > 0;

  return (
    <div className="overlay" onPointerDown={(e) => { pressedOverlay.current = e.target === e.currentTarget; }}
      onClick={(e) => e.target === e.currentTarget && pressedOverlay.current && phase !== 'saving' && setPhase('idle')}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="head"><h2>{t.upload}</h2><button onClick={() => setPhase('idle')} disabled={phase === 'saving'}>✕</button></div>
        <div className="body">
          {(phase === 'upload' || phase === 'loading') && <Dropzone t={t} busy={phase === 'loading'} onFile={addImage} />}
          {editing && (
            <>
              <div className="controls">
                {opts.orientationToggle && g.shape === 'rect' && (
                  <span className="seg">
                    <button className={orientation === 'portrait' ? 'on' : ''} onClick={() => switchOrientation('portrait')}>{t.portrait}</button>
                    <button className={orientation === 'landscape' ? 'on' : ''} onClick={() => switchOrientation('landscape')}>{t.landscape}</button>
                  </span>
                )}
                {selectedImage && (
                  <span className="seg" aria-label={t.zoom}>
                    <button onClick={() => zoom(1 / 1.1)} aria-label={`${t.zoom} ut`}>−</button>
                    <button onClick={() => zoom(1.1)} aria-label={`${t.zoom} in`}>+</button>
                  </span>
                )}
                {pickedImage && (
                  <button className={sizing ? 'icon on' : 'icon'} aria-label={t.size} title={t.size} onClick={() => setSizing(!sizing)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      {/* A frame with a dimension line beneath it. */}
                      <rect x="4" y="3" width="16" height="11" rx="1" />
                      <path d="M4 20h16M4 20l2.5-2.5M4 20l2.5 2.5M20 20l-2.5-2.5M20 20l-2.5 2.5" />
                    </svg>
                  </button>
                )}
                <button onClick={rotate}>{t.rotate} 90°</button>
                <button onClick={addText}>{t.addText}</button>
                <button onClick={() => { setAddRights(false); setAdding(true); }}>{t.addImage}</button>
                <input ref={extraInput} type="file" accept="image/*,.heic,.heif" hidden
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) addImage(f); e.target.value = ''; }} />
                <label className="small"><input type="checkbox" checked={ruler} onChange={(e) => setRuler(e.target.checked)} /> {t.ruler}</label>
              </div>
              <div className="stagewrap">
                {selectedText && (
                  <TextTools t={t} text={selectedText} sizeMin={sizeMin} sizeMax={sizeMax}
                    onChange={updateText} onDone={() => setSelected(null)} />
                )}
                {adding && (
                  <div className="texttools">
                    <div className="row"><Rights t={t} checked={addRights} onChange={setAddRights} /></div>
                    <div className="row">
                      <button className="primary" disabled={!addRights} onClick={() => { setAdding(false); extraInput.current?.click(); }}>{t.choose}</button>
                      <button onClick={() => setAdding(false)}>{t.cancel}</button>
                    </div>
                  </div>
                )}
                {sizing && pickedImage && <SizeTools t={t} im={pickedImage} g={g} onChange={updateImage} onDone={() => setSizing(false)} />}
                <Editor g={g} images={images} loaded={loaded} onImageChange={updateImage}
                  texts={texts} onTextChange={updateText} selected={selected} onSelect={setSelected}
                  onRemove={removeSelected} ruler={ruler} fontsVersion={fontsVersion} />
              </div>
            </>
          )}
          {error && <div className="err">{error}</div>}
          {phase === 'saving' && <div className="progress"><i style={{ width: `${Math.round(progress * 100)}%` }} /></div>}
        </div>
        {editing && (
          <div className="foot">
            <div className="foot__left">
              {dpi < opts.minDpi && <span className="warn">{t.lowRes} ({Math.round(dpi)} dpi)</span>}
              {uncovered && <span className="warn">{t.notCovered}</span>}
              {checkered && <span className="warn">{t.fakeTransparency}</span>}
              <button className="link" onClick={restartInModal} disabled={phase === 'saving'}>{t.restart}</button>
            </div>
            <button onClick={() => setPhase('idle')} disabled={phase === 'saving'}>{t.cancel}</button>
            <button className="primary" onClick={save} disabled={phase === 'saving'}>{phase === 'saving' ? t.saving : t.save}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Dropzone({ t, busy, onFile }: { t: ResolvedOptions['labels']; busy: boolean; onFile(f: File): void }) {
  const [over, setOver] = useState(false);
  // Nothing is picked or dropped until the customer confirms the right to use the image.
  const [rights, setRights] = useState(false);
  const ready = rights && !busy;
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className={over ? 'drop over' : 'drop'}
      onDragOver={(e) => { e.preventDefault(); setOver(ready); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files[0]; if (f && ready) onFile(f); }}
      onClick={() => ready && inputRef.current?.click()}>
      <input ref={inputRef} type="file" accept="image/*,.heic,.heif" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
      <div>{busy ? t.loading : t.dropHint}</div>
      {!busy && (
        <>
          <Rights t={t} checked={rights} onChange={setRights} />
          <button className="primary" type="button" disabled={!rights}>{t.choose}</button>
        </>
      )}
    </div>
  );
}

/** The customer confirms the right to use the image. Stops the click so a drop zone behind it stays shut. */
function Rights({ t, checked, onChange }: { t: ResolvedOptions['labels']; checked: boolean; onChange(v: boolean): void }) {
  return (
    <label className="rights" onClick={(e) => e.stopPropagation()}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{t.rights}</span>
    </label>
  );
}

/** Width and height of an image in cm, linked by its aspect. Typing one shows the other. */
function SizeTools({ t, im, g, onChange, onDone }: {
  t: ResolvedOptions['labels']; im: ImageObject; g: Geometry; onChange(im: ImageObject): void; onDone(): void;
}) {
  // A quarter turn swaps what the customer sees as width and height.
  const swapped = Math.round(im.rotation / 90) % 2 !== 0;
  const [nw, nh] = swapped ? [im.naturalHeight, im.naturalWidth] : [im.naturalWidth, im.naturalHeight];
  const cm = (px: number) => (px * im.scale) / g.pxPerMm / 10;
  const fmt = (v: number) => v.toFixed(2).replace('.', ',');
  // The field being typed in keeps its raw text; the other one always shows the computed value.
  const [draft, setDraft] = useState<{ field: 'w' | 'h'; text: string } | null>(null);
  const edit = (field: 'w' | 'h', text: string) => {
    setDraft({ field, text });
    const v = Number(text.replace(',', '.'));
    if (v > 0) onChange({ ...im, scale: (v * 10 * g.pxPerMm) / (field === 'w' ? nw : nh) });
  };
  const value = (field: 'w' | 'h') => (draft?.field === field ? draft.text : fmt(cm(field === 'w' ? nw : nh)));
  return (
    <div className="texttools">
      <div className="row">
        <label>{t.width} <input type="text" inputMode="decimal" size={6} value={value('w')} onChange={(e) => edit('w', e.target.value)} onBlur={() => setDraft(null)} /> cm</label>
        <label>{t.height} <input type="text" inputMode="decimal" size={6} value={value('h')} onChange={(e) => edit('h', e.target.value)} onBlur={() => setDraft(null)} /> cm</label>
        <button type="button" onClick={onDone}>{t.done}</button>
      </div>
      <div className="row small">{t.sheetIs} {fmt(g.trimWidthMm / 10)} × {fmt(g.trimHeightMm / 10)} cm</div>
    </div>
  );
}
